import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  User, 
  ArrowRight,
  Wrench,
  Settings
} from 'lucide-react';

const Blog = () => {
  const blogPosts = [
    {
      id: 1,
      title: "Elektrofahrzeuge: Spezielle Herausforderungen bei der Autoglas Reparatur",
      excerpt: "Elektrofahrzeuge stellen besondere Anforderungen an die Autoglas Reparatur. Erfahren Sie, warum die Reparatur von Tesla, BMW i-Serie und anderen E-Fahrzeugen spezielle Expertise erfordert.",
      content: `
        <h2>Die Besonderheiten von Elektrofahrzeugen</h2>
        <p>Elektrofahrzeuge wie Tesla Model S, BMW i4 oder Audi e-tron haben komplexere Scheibensysteme als herkömmliche Verbrenner. Die Windschutzscheiben sind oft größer und enthalten mehr Technologie, einschließlich Kameras und Sensoren für das autonome Fahren.</p>
        
        <h3>Warum E-Fahrzeuge spezielle Behandlung benötigen:</h3>
        <ul>
          <li><strong>Größere Scheiben:</strong> Mehr Fläche bedeutet höhere Belastung und präzisere Reparatur</li>
          <li><strong>Integrierte Sensoren:</strong> Kameras und Radar-Systeme erfordern präzise Kalibrierung</li>
          <li><strong>Höhere Spannungen:</strong> Sicherheitsaspekte bei der Arbeit an Hochvoltsystemen</li>
          <li><strong>Spezielle Klebstoffe:</strong> E-Fahrzeuge benötigen oft andere Klebstoffe für optimale Haftung</li>
        </ul>
        
        <h3>Unsere Expertise bei E-Fahrzeugen</h3>
        <p>Unser Team ist speziell geschult für die Reparatur von Elektrofahrzeugen. Wir arbeiten mit den neuesten Kalibrierungsgeräten und verwenden nur zugelassene Materialien für E-Fahrzeuge.</p>
      `,
      image: "https://images.unsplash.com/photo-1593941707882-a5bac6861d75?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      category: "Elektrofahrzeuge",
      author: "Auto-Glass Mannheim Team",
      date: "2024-01-15",
      readTime: "5 min",
      featured: true
    },
    {
      id: 2,
      title: "Von Kleinwagen bis Luxuslimousine: Wir reparieren alle Fahrzeugtypen",
      excerpt: "Ob Smart, VW Golf oder Rolls-Royce - wir bieten professionelle Autoglas Reparatur für alle Fahrzeugklassen. Erfahren Sie, wie wir jeden Fahrzeugtyp optimal behandeln.",
      content: `
        <h2>Universalität in der Autoglas Reparatur</h2>
        <p>Bei Auto-Glass Mannheim glauben wir daran, dass jedes Fahrzeug die beste Behandlung verdient - unabhängig von Marke, Modell oder Preisklasse. Von praktischen Kleinwagen bis hin zu exklusiven Luxuslimousinen behandeln wir jedes Fahrzeug mit der gleichen Sorgfalt und Expertise.</p>
        
        <h3>Unsere Fahrzeugpalette:</h3>
        <ul>
          <li><strong>Kleinwagen:</strong> Smart, VW Up, Fiat 500 - präzise Arbeit auf kleinem Raum</li>
          <li><strong>Kompaktwagen:</strong> VW Golf, BMW 1er, Mercedes A-Klasse - ausgewogene Qualität</li>
          <li><strong>Mittelklasse:</strong> BMW 3er, Mercedes C-Klasse, Audi A4 - technische Perfektion</li>
          <li><strong>Oberklasse:</strong> BMW 7er, Mercedes S-Klasse, Audi A8 - höchste Ansprüche</li>
          <li><strong>Luxusfahrzeuge:</strong> Rolls-Royce, Bentley, Maybach - exklusive Behandlung</li>
        </ul>
        
        <h3>Warum Universalität wichtig ist</h3>
        <p>Jeder Fahrzeugtyp hat seine eigenen Herausforderungen. Kleinwagen erfordern präzise Arbeit auf engem Raum, während Luxusfahrzeuge oft komplexe Technologie und höchste Qualitätsansprüche haben. Unser Team ist für alle diese Herausforderungen ausgebildet.</p>
      `,
      image: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      category: "Fahrzeugtypen",
      author: "Auto-Glass Mannheim Team",
      date: "2024-01-10",
      readTime: "4 min",
      featured: true
    },
    {
      id: 3,
      title: "Die Zukunft der Autoglas Reparatur: Smart Glass Technologie",
      excerpt: "Smart Glass Technologien revolutionieren die Automobilindustrie. Erfahren Sie, wie wir mit diesen neuen Technologien umgehen und welche Herausforderungen sie mit sich bringen.",
      content: `
        <h2>Smart Glass: Die nächste Generation</h2>
        <p>Smart Glass Technologien wie dimmbare Scheiben, Head-up-Displays und integrierte Displays werden immer häufiger. Diese Technologien stellen neue Herausforderungen an die Reparatur und den Austausch von Autoglas.</p>
        
        <h3>Neue Smart Glass Technologien:</h3>
        <ul>
          <li><strong>Dimmbare Scheiben:</strong> Elektrochrome Technologie für automatische Abdunklung</li>
          <li><strong>Head-up-Displays:</strong> Projektion von Informationen direkt auf die Windschutzscheibe</li>
          <li><strong>Integrierte Displays:</strong> Touchscreens und Informationsanzeigen in der Scheibe</li>
          <li><strong>Wärmereflektierende Beschichtungen:</strong> Moderne Klimatisierung durch intelligente Beschichtungen</li>
        </ul>
        
        <h3>Unsere Vorbereitung auf die Zukunft</h3>
        <p>Wir investieren kontinuierlich in neue Technologien und Schulungen, um mit den neuesten Smart Glass Entwicklungen Schritt zu halten. Unser Team wird regelmäßig in neuen Reparaturtechniken geschult.</p>
      `,
      image: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      category: "Technologie",
      author: "Auto-Glass Mannheim Team",
      date: "2024-01-05",
      readTime: "6 min",
      featured: false
    },
    {
      id: 4,
      title: "Umweltfreundliche Autoglas Reparatur: Nachhaltigkeit im Fokus",
      excerpt: "Nachhaltigkeit ist uns wichtig. Erfahren Sie, wie wir umweltfreundliche Praktiken in unsere Autoglas Reparatur integrieren und was Sie als Kunde dazu beitragen können.",
      content: `
        <h2>Nachhaltigkeit in der Autoglas Reparatur</h2>
        <p>Als verantwortungsbewusstes Unternehmen setzen wir uns für umweltfreundliche Praktiken ein. Von der Materialauswahl bis zur Entsorgung achten wir auf Nachhaltigkeit.</p>
        
        <h3>Unsere umweltfreundlichen Maßnahmen:</h3>
        <ul>
          <li><strong>Recycling:</strong> Alle Glasscherben werden fachgerecht recycelt</li>
          <li><strong>Energiesparende Technologien:</strong> Moderne Geräte mit niedrigem Energieverbrauch</li>
          <li><strong>Umweltfreundliche Materialien:</strong> Verwendung von umweltverträglichen Klebstoffen und Reinigungsmitteln</li>
          <li><strong>Digitale Prozesse:</strong> Reduzierung von Papier durch digitale Dokumentation</li>
        </ul>
        
        <h3>Was Sie als Kunde tun können</h3>
        <p>Wählen Sie Reparatur statt Austausch, wenn möglich. Eine Reparatur verbraucht weniger Ressourcen als ein kompletter Austausch. Außerdem können Sie alte Scheiben bei uns zur fachgerechten Entsorgung abgeben.</p>
      `,
      image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      category: "Nachhaltigkeit",
      author: "Auto-Glass Mannheim Team",
      date: "2023-12-28",
      readTime: "3 min",
      featured: false
    },
    {
      id: 5,
      title: "Sicherheit bei der Autoglas Reparatur: Warum Qualität entscheidend ist",
      excerpt: "Die Qualität der Autoglas Reparatur hat direkten Einfluss auf Ihre Sicherheit. Erfahren Sie, warum Sie bei der Wahl Ihres Reparaturpartners auf Qualität achten sollten.",
      content: `
        <h2>Sicherheit steht an erster Stelle</h2>
        <p>Die Windschutzscheibe ist ein wichtiges Sicherheitselement Ihres Fahrzeugs. Sie trägt zur strukturellen Integrität bei und schützt Sie bei einem Unfall. Daher ist die Qualität der Reparatur von entscheidender Bedeutung.</p>
        
        <h3>Warum Qualität so wichtig ist:</h3>
        <ul>
          <li><strong>Strukturelle Integrität:</strong> Die Windschutzscheibe trägt zur Stabilität der Fahrzeugkarosserie bei</li>
          <li><strong>Airbag-Funktion:</strong> Bei einem Unfall stützt die Scheibe den Airbag ab</li>
          <li><strong>Sichtqualität:</strong> Schlechte Reparaturen können die Sicht beeinträchtigen</li>
          <li><strong>Wasserdichtigkeit:</strong> Mangelhafte Reparaturen können zu Wassereintritt führen</li>
        </ul>
        
        <h3>Unsere Qualitätsstandards</h3>
        <p>Wir verwenden ausschließlich zugelassene Materialien und arbeiten nach den höchsten Qualitätsstandards. Jede Reparatur wird von erfahrenen Technikern durchgeführt und mehrfach kontrolliert.</p>
      `,
      image: "https://images.unsplash.com/photo-1580273916550-e323be2ae537?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      category: "Sicherheit",
      author: "Auto-Glass Mannheim Team",
      date: "2023-12-20",
      readTime: "4 min",
      featured: false
    },
    {
      id: 6,
      title: "Mobile Autoglas Reparatur: Service direkt bei Ihnen",
      excerpt: "Zeit ist kostbar. Mit unserem mobilen Service kommen wir direkt zu Ihnen und führen die Reparatur vor Ort durch. Erfahren Sie mehr über diesen praktischen Service.",
      content: `
        <h2>Mobiler Service für maximale Flexibilität</h2>
        <p>Unser mobiler Autoglas Service bietet Ihnen maximale Flexibilität und Komfort. Wir kommen direkt zu Ihnen - ob zu Hause, bei der Arbeit oder an einem anderen Ort Ihrer Wahl.</p>
        
        <h3>Vorteile des mobilen Services:</h3>
        <ul>
          <li><strong>Zeitersparnis:</strong> Keine Wartezeiten oder Fahrten zu unserer Werkstatt</li>
          <li><strong>Flexibilität:</strong> Termine auch außerhalb der normalen Geschäftszeiten</li>
          <li><strong>Komfort:</strong> Sie können während der Reparatur anderen Tätigkeiten nachgehen</li>
          <li><strong>Transparenz:</strong> Sie können den gesamten Prozess verfolgen</li>
        </ul>
        
        <h3>Unser mobiler Service umfasst:</h3>
        <p>Wir führen alle gängigen Autoglas Reparaturen mobil durch, einschließlich Steinschlag Reparatur, Scheibenaustausch und Kalibrierung von Fahrerassistenzsystemen. Unser mobiles Team ist mit allen notwendigen Geräten ausgestattet.</p>
        
        <h3>Servicegebiet</h3>
        <p>Unser mobiler Service ist in Mannheim, Frankfurt, Stuttgart und Umgebung verfügbar. Kontaktieren Sie uns für einen Termin in Ihrer Nähe.</p>
      `,
      image: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      category: "Service",
      author: "Auto-Glass Mannheim Team",
      date: "2023-12-15",
      readTime: "3 min",
      featured: false
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Auto-Glass Mannheim Blog
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Fachwissen, Tipps und Neuigkeiten aus der Welt der Autoglas Reparatur
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Posts */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Empfohlene Artikel
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Die wichtigsten Themen rund um Autoglas Reparatur und Elektrofahrzeuge
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {blogPosts.filter(post => post.featured).map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="relative h-64">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <Calendar size={16} className="mr-1" />
                    {new Date(post.date).toLocaleDateString('de-DE')}
                    <Clock size={16} className="ml-4 mr-1" />
                    {post.readTime}
                    <User size={16} className="ml-4 mr-1" />
                    {post.author}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Link
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center text-orange-600 font-medium hover:text-orange-700 transition-colors duration-200"
                  >
                    Weiterlesen
                    <ArrowRight size={16} className="ml-1" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* All Posts */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Alle Artikel
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Entdecken Sie unser vollständiges Blog-Archiv
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative h-48">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                      {post.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-3">
                    <Calendar size={16} className="mr-1" />
                    {new Date(post.date).toLocaleDateString('de-DE')}
                    <Clock size={16} className="ml-4 mr-1" />
                    {post.readTime}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>
                  <Link
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center text-orange-600 font-medium hover:text-orange-700 transition-colors duration-200"
                  >
                    Weiterlesen
                    <ArrowRight size={16} className="ml-1" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Benötigen Sie professionelle Autoglas Reparatur?
            </h2>
            <p className="text-xl text-orange-100 mb-8 max-w-3xl mx-auto">
              Kontaktieren Sie uns für ein unverbindliches Angebot. Wir sind Ihr vertrauensvoller Partner für alle Autoglas Reparaturen.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-white text-orange-600 font-bold text-lg rounded-full hover:bg-orange-50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <Wrench size={20} className="mr-2" />
                Termin vereinbaren
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center px-8 py-4 border-2 border-white text-white font-bold text-lg rounded-full hover:bg-white hover:text-orange-600 transition-all duration-300"
              >
                <Settings size={20} className="mr-2" />
                Unsere Leistungen
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Blog; 